import CryptoJS from 'crypto-js';
import axios, { AxiosRequestConfig } from 'axios';
import xml2js from 'xml2js';
import toast from './toast';
import {
  postSignRequest,
} from '../api/vault/assetUpload';
import { updateAssetStatus } from '../api/vault/assetStatus/api';
import { ActionStatus } from '../api/vault/assetStatus/interfaces';

export const parseXMLToJS = (xml: string): any => {
  let js: any;

  const parser = new xml2js.Parser({ explicitArray: false });

  parser.parseString(xml, (error: any, result: any) => {
    if (error === null) {
      js = result;
    } else {
      js = error;
    }
  });

  return js;
};

type ETag = string;
type PartNumber = number;

interface CompletedPart {
  /**
   * Entity tag returned when the part was uploaded.
   */
  ETag?: ETag;
  /**
   * Part number that identifies the part. This is a positive integer between 1 and 10,000.
   */
  PartNumber?: PartNumber;
}

const transferMBChunk = 10; // 10MB transfers..., S3 needs it to be between 5MB and 100MB

async function signRequest(url: string, verb: string, headers: string, content: any) {
  const buf = CryptoJS.lib.WordArray.create(content);
  const hash = CryptoJS.SHA256(buf);
  const contentHash = `${hash}`;

  // Call Vault API
  return postSignRequest({
    url, verb, headers, contentHash,
  });
}

async function abortUpload(fileStream: File, urlBase: string, uploadId: string, isVaultSuperAdmin: boolean) {
  try {
    const url = `${urlBase}?uploadId=${uploadId}`;

    /* Abort Multipart Upload - Signing */
    const abortResult = await signRequest(url, 'DELETE', '', '');

    const abortHeaders: any = {};

    abortResult.data.data.SignS3V4RequestResult.forEach((result: any) => {
      if (result.Key.toLowerCase() !== 'host' && result.Key.toLowerCase() !== 'content-length') { // These headers are considered unsafe to be set here
        abortHeaders[result.Key] = result.Value;
      }
    });

    const abortConfig: AxiosRequestConfig = {
      baseURL: url,
      headers: abortHeaders,
    };

    /* Abort Multipart Upload - S3 */
    await axios.delete('', abortConfig);
  } catch (e) {
    console.log('Error aborting multipart upload:', e);
  }

  if (window.EXCHANGE_UPLOAD_CANCELED) return;

  const alert = `Error on multipart import ${fileStream.name}`;
  if (!isVaultSuperAdmin) {
    toast(alert, {
      type: toast.TYPE.ERROR,
    });
  }
  console.log('Hit error uploading');
}

function readAndUploadLargeFile(fileStream: File, urlBase: string, uploadId: string, assetStatusId: string, isVaultSuperAdmin: boolean, progressUpdateCallback: (progressPercentage: string) => void = null): Promise<boolean> {
  return new Promise(async (resolve, reject) => {
    let lastPct = 0;
    const sz = fileStream.size;
    let offset = 0;
    const blockSize = 1024 * 1024 * transferMBChunk; // 10MB transfers..., S3 needs it to be between 5MB and 100MB
    const totalParts = Math.ceil(sz / blockSize);
    let processed = 0;
    const dataPacks: CompletedPart[] = [];
    const readerMap: Map<FileReader, number> = new Map();
    let totalPartsProcessed = 0;

    async function onLoad(data: ProgressEvent<FileReader>) {
      try {
        const reader = data.target;
        const partNum = readerMap.get(reader);
        const url = `${urlBase}?partNumber=${partNum}&uploadId=${uploadId}`;

        /* Upload Part - Signing */
        const uploadPartResponse = await signRequest(url, 'PUT', `content-length:${data.total}`, data.target.result);

        const uploadPartHeaders: any = {};

        uploadPartResponse.data.data.SignS3V4RequestResult.forEach((result: any) => {
          if (result.Key.toLowerCase() !== 'host' && result.Key.toLowerCase() !== 'content-length') { // These headers are considered unsafe to be set here
            uploadPartHeaders[result.Key] = result.Value;
          }
        });

        const uploadPartConfig: AxiosRequestConfig = {
          baseURL: url,
          headers: uploadPartHeaders,
        };

        /* Upload Part - S3 */
        let uploadPartResult = null;
        let retry = 0;
        while (retry < 5) {
          try {
            // eslint-disable-next-line no-await-in-loop
            uploadPartResult = await axios.put('', data.target.result, uploadPartConfig);
            break;
          } catch (error) {
            console.log(`Error uploading part ${partNum}, retry #: ${retry}, retrying...`, error);
            retry += 1;
          }
        }

        if (uploadPartResult && uploadPartResult.headers.etag) {
          const t: CompletedPart = {};
          t.ETag = uploadPartResult.headers.etag.replace(/['"]+/g, ''); // Remove double quotes coming from S3
          t.PartNumber = partNum;
          dataPacks.push(t);
        } else {
          await abortUpload(fileStream, urlBase, uploadId, isVaultSuperAdmin);
          resolve(false);
          return;
        }

        processed += data.total;

        const pct = processed * 100.0 / fileStream.size;

        if (window.EXCHANGE_UPLOAD_CANCELED) {
          await abortUpload(fileStream, urlBase, uploadId, isVaultSuperAdmin);
          readerMap.forEach((readerBlock, rdr) => rdr.abort());
          resolve(false);
          return;
        }
        if (progressUpdateCallback) progressUpdateCallback(pct.toFixed(0));

        if (pct - lastPct > 5) { // Only in 5 percent increments
          console.log(`[QA] MultipartUpload ${fileStream.name} ${pct.toFixed(0)}% complete`);
          lastPct = pct;
          await updateAssetStatus(assetStatusId, { actionProgress: `Uploading - ${lastPct.toFixed(0)}`, actionStatus: ActionStatus.InProgress });
        }

        readerMap.delete(reader);
        totalPartsProcessed += 1;

        // Check for done - only trigger when ALL parts have finished processing
        if (totalPartsProcessed === totalParts) {
          // Abort if we didn't get any ETags or we are missing any.
          if (dataPacks.length !== totalParts) {
            await abortUpload(fileStream, urlBase, uploadId, isVaultSuperAdmin);
            resolve(false);
            return;
          }

          // Sort the parts list, S3 requires it.
          dataPacks.sort((a, b) => a.PartNumber - b.PartNumber);

          // Complete
          let payloadCompleteUpload: string = '<CompleteMultipartUpload>';
          dataPacks.forEach((value) => {
            payloadCompleteUpload += '<Part>';
            payloadCompleteUpload += `<PartNumber>${value.PartNumber}</PartNumber>`;
            payloadCompleteUpload += `<ETag>${value.ETag}</ETag>`;
            payloadCompleteUpload += '</Part>';
          });
          payloadCompleteUpload += '</CompleteMultipartUpload>';

          /* Complete Multipart Upload -Signing */
          const urlCompleteUpload = `${urlBase}?uploadId=${uploadId}`;

          // Equivalent to Encoding.UTF8.GetBytes
          const enc = new TextEncoder(); // Always UTF-8

          const payloadBytes = enc.encode(payloadCompleteUpload);
          const multipartCompleteResponse = await signRequest(
            urlCompleteUpload,
            'POST',
            `content-length:${payloadBytes.length};content-type:application/xml`,
            payloadBytes,
          );

          const CompleteMultipartHeaders: any = {};

          multipartCompleteResponse.data.data.SignS3V4RequestResult.forEach((result: any) => {
            if (result.Key.toLowerCase() !== 'host' && result.Key.toLowerCase() !== 'content-length') { // These headers are considered unsafe to be set here
              CompleteMultipartHeaders[result.Key] = result.Value;
            }
          });

          CompleteMultipartHeaders['Content-Type'] = 'application/xml';

          const completeUploadConfig: AxiosRequestConfig = {
            baseURL: urlCompleteUpload,
            headers: CompleteMultipartHeaders,
          };

          /* Complete Multipart Upload S3 */
          await axios.post('', payloadCompleteUpload, completeUploadConfig);
          resolve(true);
        }
      } catch (e) {
        console.log('Error in multipart upload onLoad:', e);
        await abortUpload(fileStream, urlBase, uploadId, isVaultSuperAdmin);
        resolve(false);
      }
    }

    async function readBytes(stream: File, off: number, rdr: FileReader) {
      // Don't let the reader get too far ahead of the slicer...
      if (off - processed > blockSize * 5) {
        setTimeout(() => readBytes(stream, off, rdr), 50);
      } else {
        const slice = stream.slice(off, off + blockSize);
        rdr.readAsArrayBuffer(slice);
      }
    }

    let partNumber = 1;
    try {
      // Read blockSize chunks of file
      for (offset = 0; offset < sz; offset += blockSize) {
        const reader = new window.FileReader();
        readerMap.set(reader, partNumber);
        reader.onload = (data) => {
          onLoad(data);
        };
        readBytes(fileStream, offset, reader);
        partNumber += 1; // Increment part number
      }
    } catch {
      await abortUpload(fileStream, urlBase, uploadId, isVaultSuperAdmin);
      resolve(false);
    }
  });
}

async function GetS3UploadId(urlBase: string): Promise<string> {
  return new Promise(async (resolve, reject) => {
    const url = `${urlBase}?uploads`;

    /* Create Multipart Upload - Signing */
    const resp = await signRequest(url, 'POST', 'content-type:video/mp4', '');

    // Return if we didn't get an id from S3. No need to abort (delete parts) yet.
    if (resp.data == null) resolve('');

    const initiateMultipartUploadHeaders: any = {};

    resp.data.data.SignS3V4RequestResult.forEach((result: any) => {
      if (result.Key.toLowerCase() !== 'host' && result.Key.toLowerCase() !== 'content-length') { // These headers are considered unsafe to be set here
        initiateMultipartUploadHeaders[result.Key] = result.Value;
      }
    });

    const initiateMultipartUploadConfig: AxiosRequestConfig = {
      baseURL: url,
      headers: initiateMultipartUploadHeaders,
    };

    const responseMultipart = await axios.post('', null, initiateMultipartUploadConfig);
    const result = parseXMLToJS(responseMultipart.data);

    resolve(result.InitiateMultipartUploadResult.UploadId);
  });
}

export async function uploadMultipartFile(file: File, urlStart: string, assetStatusId: string, isVaultSuperAdmin: boolean, progressUpdateCallback: (progressPercentage: string) => void = null): Promise<boolean> {
  return new Promise(async (resolve, reject) => {
    const urlArray = urlStart.split('?'); // URL we get from prior function has query string parameters in it, remove them.
    if (urlArray.length === 0) resolve(false);

    const [urlBase] = urlArray;

    /* Create Multipart Upload - S3 and get upload id */
    const uploadId = await GetS3UploadId(urlBase);

    if (uploadId === '') resolve(false);
    resolve(await readAndUploadLargeFile(file, urlBase, uploadId, assetStatusId, isVaultSuperAdmin, progressUpdateCallback));
  });
}
